/**
 * low-pass.ts describes a low-pass filter meeting the provided specifications.
 * It provides all the methods promised by `FilterSignature`.
 */

import FilterConstants from '../../filter-constants';
import FrequencyRange from '../process-data/frequency';
import { Complex, createComplex } from '../process-data/complex';
import { createTimeAxis } from '../formulas/time-axis';
import FilterResponse from '../filter-response/filter-response';
import { SpecificationParameters, FilterSignature, FilterStage, StepResponse } from './filter';

export default {
  get name(): string {
    return FilterConstants.filterType.lowPass;
  },

  getStages(specificationParameters: SpecificationParameters): FilterStage[] {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });
    const stages = filterResponse.lowPass(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      specificationParameters.passbandFrequency,
      specificationParameters.stopbandFrequency,
      specificationParameters.filterResponseValue,
    );

    return stages.map((item: FilterStage, index: number) => {
      item.stageType = FilterConstants.filterType.lowPass;
      item.stageLetter = FilterConstants.stageLetter[index] ?? '';

      return item;
    });
  },

  getOrder(specificationParameters: SpecificationParameters): number {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });
    return filterResponse.getOrder(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      specificationParameters.passbandFrequency,
      specificationParameters.stopbandFrequency,
      specificationParameters.filterResponseValue,
    );
  },

  getNumberOfStages(specificationParameters: SpecificationParameters): number {
    const filterResponse = FilterResponse.create({ selectedResponseType: specificationParameters.filterResponseType });
    return filterResponse.getNumberOfStages(
      specificationParameters.passbandAttenuation,
      specificationParameters.stopbandAttenuation,
      specificationParameters.passbandFrequency,
      specificationParameters.stopbandFrequency,
      specificationParameters.filterResponseValue,
    );
  },

  getFilterFrequencyRange(stopbandFrequency: number, passbandFrequency: number): FrequencyRange {
    const startFrequency = passbandFrequency / 10;
    const stopFrequency = stopbandFrequency * 10;
    const decade = Math.log10(stopFrequency) - Math.log10(startFrequency);

    const result = FrequencyRange.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: 100,
      decade: decade,
    });

    return result;
  },

  getActualStageSpecFrequency(loopFp: number): FrequencyRange {
    const startFrequency = loopFp / 5;
    const stopFrequency = loopFp * 2;

    const result = FrequencyRange.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: 50,
    });

    return result;
  },

  createFirstOrderFilterAmplitude(fp: number, gain: number, frequencyArray: number[]): Complex[] {
    return frequencyArray.map((frequency) => {
      const complexGain = createComplex(gain, 0);
      const complexDenom = createComplex(1, frequency / fp);
      return complexGain.divide(complexDenom);
    });
  },

  createSecondOrderFilterAmplitude(fp: number, Q: number, gain: number, frequencyArray: number[]): Complex[] {
    return frequencyArray.map((frequency) => {
      const complexGain = createComplex(gain, 0);
      const complexDenom = createComplex(
        (fp * fp - frequency * frequency) / (fp * fp),
        (frequency * fp) / Q / (fp * fp),
      );
      return complexGain.divide(complexDenom);
    });
  },

  /**
   * stopbandFrequency in params was removed to satisfy linting rules.
   */
  createTimeAxis(passbandFrequency: number): number[] {
    return createTimeAxis((passbandFrequency / 10) * 10);
  },

  setupFirstOrderStepResponse(stageFp: number, gain: number): StepResponse {
    return { a: gain, b: gain, c: 0, w0: stageFp * 2 * Math.PI, Q: 0 };
  },

  setupSecondOrderStepResponse(stageFp: number, stageQ: number, gain: number): StepResponse {
    const result = { w0: stageFp * 2 * Math.PI, Q: stageQ, a: 0, b: 0, c: 0 };
    result.c = gain * result.w0 * result.w0;
    return result;
  },

  getImpulseArrayFirstElement(): number {
    return 0;
  },

  getSecondOrderStageImplementation(stageFp: number, stageQ: number, gain: number): string {
    let result = FilterConstants.implementationType.sallenKey;
    if (
      stageFp * gain * (0.5 + stageQ) > FilterConstants.Limits.INDUCTOR_PREFERRED_FREQ_X_GAIN_X_Q &&
      stageFp > FilterConstants.Limits.INDUCTOR_ALLOWED_FREQ
    ) {
      result = FilterConstants.implementationType.inductor;
    }

    return result;
  },

  //getMaxFrequency used in voltage range calculations.  We don't look at magnitudes beyond this frequency for voltage range because we don't care if they get clipped.
  getMaxFrequency(stopbandFrequency: number): number {
    return stopbandFrequency;
  },

  getAdcFrequency(stopFrequency: number): number {
    return stopFrequency / 10;
  },
} as FilterSignature;
